import React, { useState, useContext } from 'react';
import {
  Box, Button, FormControl, FormLabel, Input, Textarea, VStack, Heading, Flex, Text, Radio, RadioGroup, Stack
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';

const CodingGuidelines = () => {
  const { accessToken } = useContext(AppContext);
  const [specialty, setSpecialty] = useState('');
  const [diagnosisPhilosophy, setDiagnosisPhilosophy] = useState('');
  const [cptRules, setCptRules] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!specialty.trim() || !diagnosisPhilosophy.trim()) {
      toast({
        title: "Required fields are missing.",
        description: "Specialty and Diagnosis Philosophy are required.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
      return;
    }
    setIsLoading(true);

    try {
      const response = await callApi('/onboarding/coding-guidelines', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          specialty,
          diagnosis_philosophy: diagnosisPhilosophy,
          cpt_rules: cptRules,
        }),
      });

      if (response.ok) {
        toast({
          title: "Guidelines saved.",
          description: "Thank you! You will now be redirected.",
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top"
        });
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save guidelines.");
      }
    } catch (error) {
      toast({
        title: "An error occurred.",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top"
      });
      setIsLoading(false);
    }
  };

  return (
      <Flex align="center" justify="center" minH="100vh" bg="gray.50">
        <Box bg="white" p={8} maxWidth="800px" borderWidth={1} borderRadius={8} boxShadow="lg" w="100%">
          <VStack spacing={6} as="form" onSubmit={handleSubmit}>
            <Heading as="h1" size="lg" textAlign="center">Set Up Your Coding Guidelines</Heading>
            <Text textAlign="center" color="gray.600">
              Your answers will help us tailor our AI to match your specific coding and documentation requirements.
            </Text>
            <FormControl isRequired>
              <FormLabel>Primary Specialty</FormLabel>
              <Input
                type="text"
                placeholder="e.g., General Medicine, Gastroenterology, E/M Office Visits"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Diagnosis (ICD-10) Philosophy</FormLabel>
              <RadioGroup onChange={setDiagnosisPhilosophy} value={diagnosisPhilosophy}>
                <Stack direction="column">
                  <Radio value="conservative">
                    <Text fontSize="sm" pl={2}>
                      **Conservative:** Follow standard coding guidelines and assume no additional documentation is available. This will limit suggestions to clear, definitive diagnoses only.
                    </Text>
                  </Radio>
                  <Radio value="investigative">
                    <Text fontSize="sm" pl={2}>
                      **Investigative:** Identify potential diagnoses based on available evidence, which could require additional documentation or clinical review to confirm.
                    </Text>
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Procedure (CPT) Coding Rules (Optional)</FormLabel>
              <Textarea
                placeholder="Provide any general CPT coding rules for your specialty. You can include information on modifier usage, primary procedure selection, or payer-specific rules."
                value={cptRules}
                onChange={(e) => setCptRules(e.target.value)}
                rows={4}
              />
            </FormControl>
            <Button
              type="submit"
              colorScheme="blue"
              width="full"
              isLoading={isLoading}
              size="lg"
            >
              Save Guidelines and Continue
            </Button>
          </VStack>
        </Box>
      </Flex>
  );
};

export default CodingGuidelines;